/**
 * /reset-password?token=... — define a nova senha a partir do link do e-mail.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../utils/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "../components/ui/sonner";
import { accentBg } from "../utils/accent";
import { Eye, EyeOff, Lock, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const RULES = [
  { test: (v) => v.length >= 8, label: "Mínimo de 8 caracteres" },
  { test: (v) => /[A-Z]/.test(v), label: "1 letra maiúscula" },
  { test: (v) => /[0-9]/.test(v), label: "1 número" },
  { test: (v) => /[^A-Za-z0-9]/.test(v), label: "1 caractere especial" },
];

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const check = useCallback(async () => {
    if (!token) {
      setTokenError("Link inválido. Solicite um novo e-mail de redefinição.");
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get(`/auth/reset-password/${token}`);
      setEmail(data.email || "");
    } catch (err) {
      setTokenError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => { check(); }, [check]);

  const allOk = RULES.every((r) => r.test(password));

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return toast.error("A senha não atende à política de segurança.");
    if (password !== confirm) return toast.error("As senhas não conferem.");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Senha redefinida", { description: "Faça login com a nova senha." });
      navigate("/login");
    } catch (err) {
      toast.error("Não foi possível redefinir", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7f8] flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-[440px]">
        <img src="/logo_light.png" alt="AutEM" className="h-11 object-contain mx-auto mb-8" />

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="h-[5px] flex">
            <div className="flex-1" style={{ backgroundColor: "#00b1e8" }} />
            <div className="w-20" style={{ backgroundColor: "#ff9945" }} />
          </div>

          {checking ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : tokenError ? (
            <div className="p-8 text-center" data-testid="reset-token-error">
              <div className="h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={22} />
              </div>
              <h1 className="text-lg font-bold text-gray-900">Link inválido ou expirado</h1>
              <p className="text-sm text-gray-500 mt-1">{tokenError}</p>
              <Button
                className="mt-5 w-full h-11 text-white"
                style={accentBg()}
                onClick={() => navigate("/login")}
                data-testid="reset-back-to-login"
              >
                Voltar para o login
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="p-8">
              <div className="h-10 w-10 rounded-xl bg-[#edf9fd] text-[#0086ad] flex items-center justify-center mb-4">
                <ShieldCheck size={20} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Criar nova senha</h1>
              <p className="text-sm text-gray-500 mt-1">
                Definindo a senha de acesso de <strong className="text-gray-700">{email}</strong>.
              </p>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="text-gray-700 text-sm font-medium mb-2 block">Nova senha</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Digite a nova senha"
                      className="h-12 rounded-xl pl-11 pr-12"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700 text-sm font-medium mb-2 block">Confirmar senha</Label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      type={showPw ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Repita a nova senha"
                      className="h-12 rounded-xl pl-11"
                      data-testid="reset-password-confirm"
                    />
                  </div>
                </div>

                <ul className="space-y-1.5 pt-1">
                  {RULES.map((r) => {
                    const ok = r.test(password);
                    return (
                      <li key={r.label} className="flex items-center gap-2 text-xs">
                        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-gray-300"}`} />
                        <span className={ok ? "text-emerald-600" : "text-gray-500"}>{r.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="mt-6 w-full h-12 rounded-xl text-white font-semibold"
                style={accentBg()}
                data-testid="reset-password-submit"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : "Redefinir senha"}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          AutEM — Feito para quem lidera!
        </p>
        <p className="mt-1 text-center text-[10px] text-gray-300">
          © {new Date().getFullYear()} Satellitus Tecnologia · Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
